import React, { Fragment, FunctionComponent, ReactNode, useEffect } from 'react';
import get from 'lodash/get';
import { InjectedIntl } from 'react-intl';
import { Col, Row } from 'react-flexbox-grid';
import { isObject } from 'utils';
import EditIcon from 'assets/icons/edit';
import { getTextFactory } from 'utils/TextUtils';
import Heading from 'components/Heading';
import { ProductFormValue } from 'components/productFormFields';
import { EMPLOYMENT_VALUES } from '../../Employment/constants';
import { EmploymentSummarySlice } from '../types';
import { Link } from '@nbc-design/link';
import ProInvolvementSection from './ProInvolvementSection';
import './styles.scss';
import { Alert } from '@nbc-design/alert';
import { KycQuestionData } from 'hooks/useKycQuestion/useKycQuestion';
import { resolveRefDataLabel } from 'hooks/useKycQuestion/utils';

export type DetailsSectionProps = {
  intl: InjectedIntl;
  data: {};
  name: string;
  goToModify: () => void;
  onSectionBlockedChange?: (blocked: boolean) => void;
  /** Optional per-field label overrides (e.g. dynamic KYC titles), keyed by field name. */
  labelOverrides?: Record<string, KycQuestionData>;
};

const DetailsSection: FunctionComponent<DetailsSectionProps> = (props: DetailsSectionProps) => {
  const { intl, data, name, goToModify, onSectionBlockedChange, labelOverrides } = props;

  const getAccessibilityText = getTextFactory(intl, 'accessibility.icon');
  const getSummaryText = getTextFactory(intl, 'summary');

  const trans = (suffix: string, prefix = `summary.${name}`): string => {
    suffix = suffix === 'otherPersonFirstName' || suffix === 'proInvolvementFirstName' ? 'firstName' : suffix;
    suffix = suffix === 'otherPersonLastName' || suffix === 'proInvolvementLastName' ? 'lastName' : suffix;
    suffix = suffix === 'otherPersonStatus' ? 'status' : suffix;

    if (
      suffix === 'firstName' ||
      suffix === 'lastName' ||
      suffix === 'middleName' ||
      suffix === 'currentAddress' ||
      suffix === 'email' ||
      suffix === 'birthDate' ||
      suffix === 'none'
    ) {
      prefix = 'global';
    }

    if (prefix === 'identityCheckBnc') {
      prefix = 'identityVerificationBNC';
    }

    if (prefix === 'identityCheck') {
      prefix = 'specificsBankingInfo';
    }

    return intl.formatMessage({ id: `${prefix}.${suffix}` });
  };

  const toggleContent = (): void => {
    const element: HTMLDivElement | null = document.querySelector(`.${name}Details .section__content`);
    element && element.classList.toggle('close');
  };

  const renderFiscalResidency = (data: {}, prefix = 'mainResidency'): ReactNode => {
    return Object.keys(data).map(
      (key: string): ReactNode => {
        const label = trans(key, name);
        const lg = label && label.length > 40;
        const keyName = `${name}.${prefix}.${key}`;
        const fieldName = isObject(data[key]) ? `${keyName}.${intl.locale}` : keyName;

        return (
          <Col key={keyName} xs={12} md={lg ? 12 : 6} lg={lg ? 12 : 4} className="field" data-test={`field_${keyName}`}>
            <ProductFormValue name={fieldName} label={label} />
          </Col>
        );
      },
    );
  };

  const renderList = (data: [], key: string): ReactNode => {
    return data.map((fieldName: {}, index: number) => [
      <Col xs={12} md={12} lg={12} key={index}>
        {index === 0 && key === 'secondaryResidency' && (
          <p data-test={`field_${key}`} className="list">
            {trans('secondaryCountry', 'tax')}
          </p>
        )}
      </Col>,
      renderFiscalResidency(fieldName, `${key}[${index}]`),
    ]);
  };

  const renderPhoneNumbers = (phoneNumbers: string[], key: string, fieldName: string, label: string): ReactNode => {
    return (
      <Fragment>
        {phoneNumbers.map((_, index) => (
          <Col key={key} xs={12} md={6} lg={4} className="field" data-test={`field_name_${fieldName}`}>
            <ProductFormValue name={`${fieldName}[${index}]`} label={label} />
          </Col>
        ))}
      </Fragment>
    );
  };

  const renderMainHolderName = (
    holderData: {
      mainHolderName: {
        firstName: string;
        lastName: string;
      };
      oppositePartyRole: {
        code: string;
        en: string;
        fr: string;
      };
    },
    key: string,
    fieldName: string,
    label: string,
  ): ReactNode => {
    return (
      <Fragment>
        <Col key={key} xs={12} md={6} lg={4} className="field" data-test={`field_name_${fieldName}`}>
          <ProductFormValue
            name={'personalInformation.oppositePartyRole'}
            label={`${label}${holderData.mainHolderName.firstName} ${holderData.mainHolderName.lastName}?`}
          />
        </Col>
      </Fragment>
    );
  };

  const NULLABLE_EMPLOYMENT_FIELDS = ['status', 'employerAddress', 'period'];
  const NULLABLE_FIELDS_BY_SECTION: Record<string, string[]> = {
    employment: NULLABLE_EMPLOYMENT_FIELDS,
    employmentV2: NULLABLE_EMPLOYMENT_FIELDS,
  };
  const isEmploymentSection = name === 'employment' || name === 'employmentV2';

  const isOccupationUnavailable: boolean = (() => {
    if (!isEmploymentSection || !data || Object.keys(data as {}).length === 0) return false;
    const status = (data as EmploymentSummarySlice).status;
    return !status || (!status.en && !status.fr);
  })();

  useEffect(() => {
    onSectionBlockedChange?.(isOccupationUnavailable);
  }, [isOccupationUnavailable, onSectionBlockedChange]);

  const renderContent = (data: any, fieldNamePrefix: string): ReactNode => {
    const sectionNullableFields = NULLABLE_FIELDS_BY_SECTION[name] ?? [];
    const normalizedData = sectionNullableFields.length
      ? { ...Object.fromEntries(sectionNullableFields.map((f) => [f, null])), ...data }
      : data;

    return Object.keys(normalizedData).map(
      (key: string): ReactNode => {
        if (key === 'editable') return null;
        if (key === 'proInvolvement')
          return (
            <ProInvolvementSection
              key={key}
              intl={intl}
              proInvolvement={normalizedData[key]}
              labelOverrides={labelOverrides}
            />
          );

        let label = labelOverrides?.[key]?.title || trans(key, name);

        const lg = !!label && label.length > 40;
        const fieldName = `${fieldNamePrefix}.${isObject(normalizedData[key]) ? `${key}.${intl.locale}` : key}`;
        const dataTestlabel = `${fieldNamePrefix}_${key}`;

        if (name === 'tax' && key === 'mainResidency') return renderFiscalResidency(normalizedData[key]);
        if (name === 'tax' && key === 'secondaryResidency') return renderList(normalizedData[key], key);
        if (name === 'personalInformation' && key === 'phoneNumber') {
          return renderPhoneNumbers(normalizedData[key], key, fieldName, label);
        }

        if (name === 'personalInformation' && key === 'oppositePartyRole') {
          return null;
        }
        if (name === 'personalInformation' && key === 'mainHolderName') {
          return renderMainHolderName(normalizedData, key, fieldName, label);
        }

        if (name === 'identityCheck' && key === 'bankingAccount')
          return renderFiscalResidency(normalizedData[key], key);

        if (
          isEmploymentSection &&
          key === 'employerName' &&
          get(normalizedData, ['status', 'code'], '') === EMPLOYMENT_VALUES.SELF_EMPLOYED
        ) {
          label = trans('companyName', name);
        }

        // Show "— Not available —" only for fields the section declares as nullable
        if (sectionNullableFields.includes(key)) {
          const value = normalizedData[key];
          const isEmpty = value == null || value === '' || (isObject(value) && !value[intl.locale] && !value.code);
          if (isEmpty) {
            return (
              <Col key={key} xs={12} md={lg ? 12 : 6} lg={lg ? 12 : 6} className="field" data-test={dataTestlabel}>
                <ProductFormValue
                  name={fieldName}
                  label={label}
                  displayedValue={getSummaryText('alert.employment.notAvailable')}
                  className="summary-form-value-section not-available-value"
                />
              </Col>
            );
          }
        }

        const keyRefData = labelOverrides?.[key]?.refData;
        const resolvedDisplayValue = resolveRefDataLabel(normalizedData[key], keyRefData, intl.locale);

        return (
          <Col key={key} xs={12} md={lg ? 12 : 6} lg={lg ? 12 : 6} className="field" data-test={dataTestlabel}>
            <ProductFormValue
              name={fieldName}
              label={label}
              {...(resolvedDisplayValue !== undefined ? { displayedValue: resolvedDisplayValue } : {})}
              refData={keyRefData}
              className="summary-form-value-section"
            />
          </Col>
        );
      },
    );
  };

  // do not render empty sections
  if (!data || Object.keys(data).length === 0) {
    return null;
  }

  return (
    <div data-test={`${name}-detailsSection`} className={`${name}Details summary__section`}>
      <Row className="section__header">
        <Col xs={11} md={11} xl={11} onClick={toggleContent} className="section__title">
          <Heading type="h3" size={5} data-test="label_page_title">
            {trans('pageTitle')}
          </Heading>
        </Col>
        {(data as any).editable && (
          <Col xs={1} md={1} xl={1} onClick={goToModify} className="section__icon" data-test="image_edit_icon">
            <Link href="#" underlined={false}>
              <EditIcon className="editIcon" title={getAccessibilityText('edit')} />
            </Link>
          </Col>
        )}
      </Row>
      {isOccupationUnavailable && (
        <Alert
          id="occupation-unavailable-alert"
          title={getSummaryText('alert.employment.occupationUnavailable.title')}
          description={getSummaryText('alert.employment.occupationUnavailable.description')}
          ariaLabelIcon={getAccessibilityText('alert')}
          appearance="warning"
          className="mb-4"
        />
      )}
      <Row className={`section__content`}>{renderContent(data, name)}</Row>
    </div>
  );
};

export default DetailsSection;
